import type {
  CreateDynamicPricingDto,
  CreateDynamicPricingResponse,
} from 'components/dynamic-pricing/types'

import type { PaginatedQueryParams, PaginatedResponse } from '../types'
import type { SuggestedPriceRuleSetDto } from '../types/dynamic-pricing'

import { baseApi } from '.'

const dynamicPricingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    suggestedPriceRuleSetCount: builder.query<
      {
        total: number
        types: {
          product?: number
          brand?: number
          category?: number
          collection?: number
        }
      },
      void
    >({
      query: () => ({
        url: '/dynamic-pricing/suggested-price-rule/counts',
        method: 'GET',
      }),
      providesTags: ['DynamicPricingRuleSetCount'],
    }),
    suggestedPriceRuleCountWithTotalPrice: builder.query<
      { total: number },
      void
    >({
      query: () => ({
        url: '/dynamic-pricing/suggested-price-rule/counts/total-price',
        method: 'GET',
      }),
    }),
    suggestedPriceRuleSetTemplates: builder.query<any, PaginatedQueryParams>({
      query: (params) => ({
        url: '/dynamic-pricing/templates/me',
        method: 'GET',
        params,
      }),
      providesTags: ['DynamicPricingRuleSetTemplates'],
    }),
    suggestedPriceRuleSetTemplateById: builder.query<any, { id: number }>({
      query: ({ id }) => ({
        url: `/dynamic-pricing/templates/${id}`,
        method: 'GET',
      }),
    }),
    createFromSuggestedPriceRuleSetTemplate: builder.mutation<
      any,
      { id: number }
    >({
      query: (body) => ({
        url: `/dynamic-pricing/templates/me/${body.id}`,
        method: 'POST',
      }),
      invalidatesTags: [
        'DynamicPricingRuleSet',
        'DynamicPricingRuleSetCount',
        'DynamicPricingRuleSetTemplates',
      ],
      transformErrorResponse: (response: any) => ({
        message: response?.status === 400 ? response.data.message : undefined,
      }),
    }),
    customizeSuggestedPriceRuleSetTemplate: builder.mutation<
      any,
      {
        id: number
        payload: any
      }
    >({
      query: ({ id, payload }) => ({
        url: `/dynamic-pricing/templates/me/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [
        'DynamicPricingRuleSet',
        'DynamicPricingRuleSetCount',
        'DynamicPricingRuleSetTemplates',
      ],
      transformErrorResponse: (response: any) => ({
        message: response?.status === 400 ? response.data.message : undefined,
      }),
    }),
    suggestedPriceRuleSetById: builder.query<any, { id: number }>({
      query: ({ id }) => ({
        url: `/dynamic-pricing/${id}`,
        method: 'GET',
      }),
      providesTags: ['DynamicPricingRuleSet'],
    }),
    suggestedPriceBrandRules: builder.query<
      PaginatedResponse<SuggestedPriceRuleSetDto>,
      PaginatedQueryParams & { excludeTemplates?: boolean }
    >({
      query: (params) => ({
        url: '/dynamic-pricing/suggested-price-rule/brand',
        method: 'GET',
        params,
      }),
      providesTags: ['DynamicPricingRuleSet'],
    }),
    suggestedPriceCategoryRules: builder.query<
      PaginatedResponse<SuggestedPriceRuleSetDto>,
      PaginatedQueryParams & { excludeTemplates?: boolean }
    >({
      query: (params) => ({
        url: '/dynamic-pricing/suggested-price-rule/category',
        method: 'GET',
        params,
      }),
      providesTags: ['DynamicPricingRuleSet'],
    }),
    suggestedPriceCollectionRules: builder.query<
      PaginatedResponse<SuggestedPriceRuleSetDto>,
      PaginatedQueryParams & { excludeTemplates?: boolean }
    >({
      query: (params) => ({
        url: '/dynamic-pricing/suggested-price-rule/collection',
        method: 'GET',
        params,
      }),
      providesTags: ['DynamicPricingRuleSet'],
    }),

    suggestedPriceProductRules: builder.query<
      PaginatedResponse<SuggestedPriceRuleSetDto>,
      PaginatedQueryParams & { excludeTemplates?: boolean }
    >({
      query: (params) => ({
        url: '/dynamic-pricing/suggested-price-rule/product',
        method: 'GET',
        params,
      }),
      providesTags: ['DynamicPricingRuleSet'],
    }),
    addNewRule: builder.mutation<
      CreateDynamicPricingResponse,
      CreateDynamicPricingDto
    >({
      query: (body) => ({
        url: '/dynamic-pricing',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'DynamicPricingRuleSet',
        'DynamicPricingRuleSetCount',
        'DynamicPricingRuleSetTemplates',
      ],
    }),
    editRule: builder.mutation<
      CreateDynamicPricingResponse,
      { ruleSetId: number; rules: any; ruleSetTemplateId?: number }
    >({
      query: ({ ruleSetId, rules, ruleSetTemplateId }) => ({
        url: `/dynamic-pricing/${ruleSetId}`,
        method: 'PATCH',
        body: { ...rules, ...(ruleSetTemplateId ? { ruleSetTemplateId } : {}) },
      }),
      invalidatesTags: [
        'DynamicPricingRuleSet',
        'DynamicPricingRuleSetCount',
        'DynamicPricingRuleSetTemplates',
      ],
    }),

    deleteRule: builder.mutation<any, { id: number | undefined }>({
      query: (body) => ({
        url: `/dynamic-pricing/${body.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'DynamicPricingRuleSet',
        'DynamicPricingRuleSetCount',
        'DynamicPricingRuleSetTemplates',
      ],
    }),
    toggleRule: builder.mutation<any, { id: number }>({
      query: ({ id }) => ({
        url: `/dynamic-pricing/toggle/${id}`,
        method: 'POST',
      }),
      invalidatesTags: [
        'DynamicPricingRuleSet',
        'DynamicPricingRuleSetCount',
        'DynamicPricingRuleSetTemplates',
      ],
    }),
    calculateLiveSmartPriceForProduct: builder.mutation<any, any>({
      query: ({ id, payload }) => ({
        url: `/dynamic-pricing/calculate-live/${id}`,
        method: 'POST',
        body: payload,
      }),
    }),
    calculateSmartPriceForProduct: builder.query<any, number>({
      query: (id: number) => ({
        url: `/dynamic-pricing/calculate/${id}`,
        method: 'GET',
      }),
      providesTags: ['SmartPriceByProduct'],
    }),
    deleteRulesWithTotalPrice: builder.mutation<any, void>({
      query: () => ({
        url: `/dynamic-pricing/total-price`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'DynamicPricingRuleSet',
        'DynamicPricingRuleSetCount',
        'DynamicPricingRuleSetTemplates',
      ],
    }),
    switchRulesWithTotalPrice: builder.mutation<any, void>({
      query: () => ({
        url: `/dynamic-pricing/total-price/`,
        method: 'PUT',
      }),
      invalidatesTags: [
        'DynamicPricingRuleSet',
        'DynamicPricingRuleSetCount',
        'DynamicPricingRuleSetTemplates',
      ],
    }),
    getIsInFreezePeriod: builder.query<boolean, void>({
      query: () => ({
        url: `/cycles/is-running`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useSuggestedPriceRuleSetCountQuery,
  useSuggestedPriceRuleSetTemplatesQuery,
  useCreateFromSuggestedPriceRuleSetTemplateMutation,
  useCustomizeSuggestedPriceRuleSetTemplateMutation,
  useLazySuggestedPriceRuleSetTemplateByIdQuery,
  useLazySuggestedPriceRuleCountWithTotalPriceQuery,
  useSuggestedPriceBrandRulesQuery,
  useSuggestedPriceCategoryRulesQuery,
  useSuggestedPriceCollectionRulesQuery,
  useSuggestedPriceProductRulesQuery,
  useSuggestedPriceRuleSetByIdQuery,
  useLazySuggestedPriceRuleSetByIdQuery,
  useAddNewRuleMutation,
  useEditRuleMutation,
  useDeleteRuleMutation,
  useToggleRuleMutation,
  useCalculateLiveSmartPriceForProductMutation,
  useLazyCalculateSmartPriceForProductQuery,
  useDeleteRulesWithTotalPriceMutation,
  useSwitchRulesWithTotalPriceMutation,
  useGetIsInFreezePeriodQuery,
} = dynamicPricingApi
